/**
 * Sticky header with show/hide on scroll
 * Automatically hides header when scrolling down and shows when scrolling up or near top
 */

export class StickyHeader extends HTMLElement {
  static TRANSITION = 'top 0.3s ease'
  static UI_THRESHOLD = 200
  static SCROLL_DELAY = 300
  static FIRST_SECTION_SELECTOR = '#MainContent > .shopify-section'
  static TRANSPARENT_HEADER_SELECTOR = '.transparent'
  static UI_VISIBLE_SELECTOR = '[data-ui~="visible"]'
  static UI_SCROLLED_SELECTOR = '[data-scrolled~="true"]'
  static MENU_ITEM_SELECTOR = '[data-header-menu-item]'
  lastScrollY: number
  ticking: boolean
  isVisible: boolean
  isScrolled: boolean
  static VISIBLE_POSITION: string
  topOffset: number
  elementHeight: number
  scrollThreshold!: number
  uiThreshold!: number
  headerElement: HTMLElement | null
  scrolledTimeout: ReturnType<typeof setTimeout> | null = null
  ignoreDirectionUntil = 0
  lastDocHeight = 0
  boundMenuItemEnter: ((e: MouseEvent) => void) | null = null
  boundResize: (() => void) | null = null

  constructor() {
    super()

    // State variables
    this.lastScrollY = window.scrollY
    this.ticking = false
    this.isVisible = true
    this.isScrolled = false

    this.style.transform = StickyHeader.VISIBLE_POSITION
    this.style.transition = StickyHeader.TRANSITION
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', ''))
    this.headerElement = this.querySelector('#header')
    this.elementHeight = this.headerElement ? this.headerElement.getBoundingClientRect().height : this.getBoundingClientRect().height
    this.updateThresholds()
    // Bind scroll handler
    this.handleScroll = this.handleScroll.bind(this)
    this.handleMouseLeave = this.handleMouseLeave.bind(this)
    this.boundMenuItemEnter = this.handleMenuItemEnter.bind(this)
    this.boundResize = this.handleResize.bind(this)
  }

  updateThresholds() {
    const firstSection = document.querySelector<HTMLElement>(StickyHeader.FIRST_SECTION_SELECTOR)
    const isTransparentHeaderSection = firstSection?.querySelector(StickyHeader.TRANSPARENT_HEADER_SELECTOR) != null

    if (firstSection && isTransparentHeaderSection) {
      const firstSectionHeight = firstSection.getBoundingClientRect().height
      this.scrollThreshold = firstSectionHeight
      this.uiThreshold = firstSectionHeight
    } else {
      this.scrollThreshold = window.innerHeight / 2
      this.uiThreshold = StickyHeader.UI_THRESHOLD
    }
  }

  handleResize() {
    this.updateThresholds()
  }

  get hiddenPosition() {
    return `-${this.elementHeight + this.topOffset}px`
  }

  get visiblePosition() {
    return `${this.topOffset}px`
  }

  connectedCallback() {
    this.topOffset = parseInt(window.getComputedStyle(this).top.replace('px', ''))
    this.headerElement = this.querySelector('#header')
    this.elementHeight = this.headerElement ? this.headerElement.getBoundingClientRect().height : this.getBoundingClientRect().height
    this.updateThresholds()
    this.lastDocHeight = document.documentElement.scrollHeight

    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('resize', this.boundResize!, { passive: true })
    this.addEventListener('mouseleave', this.handleMouseLeave)
    this.addEventListener('mouseover', this.boundMenuItemEnter!)
  }

  disconnectedCallback() {
    if (this.scrolledTimeout) {
      clearTimeout(this.scrolledTimeout)
      this.scrolledTimeout = null
    }
    window.removeEventListener('scroll', this.handleScroll)
    this.removeEventListener('mouseleave', this.handleMouseLeave)
    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize)
    }
    if (this.boundMenuItemEnter) {
      this.removeEventListener('mouseover', this.boundMenuItemEnter)
    }
  }

  handleMouseLeave() {
    this.clearOpenSubmenu()
  }

  handleMenuItemEnter(e: MouseEvent) {
    const item = (e.target as HTMLElement).closest<HTMLElement>(StickyHeader.MENU_ITEM_SELECTOR)
    if (item) {
      this.setOpenSubmenu(item)
    }
  }

  setOpenSubmenu(menuItem: HTMLElement) {
    if (menuItem.getAttribute('data-submenu-open') === 'true') return
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
    menuItem.setAttribute('data-submenu-open', 'true')
  }

  clearOpenSubmenu() {
    this.querySelectorAll(StickyHeader.MENU_ITEM_SELECTOR).forEach((el) => {
      el.removeAttribute('data-submenu-open')
    })
  }

  checkForVisibleUI() {
    return this.querySelector(StickyHeader.UI_VISIBLE_SELECTOR)
  }

  updateHeaderHeightVariable(isVisible: boolean) {
    document.documentElement.style.setProperty(
      '--height-header-adapt',
      isVisible ? `${this.elementHeight}px` : '0px'
    )
  }

  showHeader() {
    if (!this.isVisible) {
      this.style.top = this.visiblePosition
      this.isVisible = true
      this.setAttribute('data-visible', 'visible')
      this.updateHeaderHeightVariable(true)
      this.suppressDirectionDetection()
    }
  }

  hideHeader() {
    if (this.isVisible && !this.checkForVisibleUI()) {
      this.style.top = this.hiddenPosition
      this.isVisible = false
      this.removeAttribute('data-visible')
      this.updateHeaderHeightVariable(false)
      this.suppressDirectionDetection()
    }
  }

  /**
   * Toggling visibility can change document height (e.g. a sub-bar collapsing),
   * which nudges scrollY and would otherwise be misread as a user scroll,
   * flipping the header back and causing flicker. Ignore direction detection
   * for the duration of the visibility transition.
   */
  suppressDirectionDetection() {
    this.ignoreDirectionUntil = performance.now() + StickyHeader.SCROLL_DELAY
  }

  registerScroll() {
    if (this.isScrolled) return
    this.isScrolled = true
    this.scheduleScrolledState(true)
  }

  unregisterScroll() {
    if (!this.isScrolled) return
    this.isScrolled = false
    this.scheduleScrolledState(false)
  }

  scheduleScrolledState(scrolled: boolean) {
    if (this.scrolledTimeout) clearTimeout(this.scrolledTimeout)
    this.scrolledTimeout = setTimeout(() => {
      if (scrolled) {
        this.setAttribute('data-scrolled', 'true')
      } else {
        this.removeAttribute('data-scrolled')
      }
      this.scrolledTimeout = null
    }, StickyHeader.SCROLL_DELAY)
  }

  handleScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const currentScroll = window.scrollY
        const currentDocHeight = document.documentElement.scrollHeight
        const docHeightChanged = currentDocHeight !== this.lastDocHeight
        this.lastDocHeight = currentDocHeight

        if (currentScroll < this.scrollThreshold) {
          this.unregisterScroll()
        } else {
          this.registerScroll()
        }

        if (docHeightChanged) {
          this.suppressDirectionDetection()
        }

        if (docHeightChanged || performance.now() < this.ignoreDirectionUntil) {
          this.lastScrollY = currentScroll
          this.ticking = false
          return
        }

        if (currentScroll < this.uiThreshold || (currentScroll < this.lastScrollY && currentScroll > this.uiThreshold)) {
          this.showHeader()
        } else if (currentScroll > this.lastScrollY && currentScroll > this.uiThreshold) {
          this.hideHeader()
        }
        this.lastScrollY = currentScroll
        this.ticking = false
      })

      this.ticking = true
    }
  }
}
